import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles, Loader2, Mic, MicOff, Volume2, VolumeX, RefreshCw, CalendarCheck, History, PlusCircle, Eye, Play, LogOut, Mail, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import {
    jarvisCreateSession,
    jarvisSendMessage,
    jarvisGetSession,
    jarvisSummarize,
    jarvisFinalize,
    jarvisListSessions,
    jarvisGetMySession,
    jarvisContinueSession,
} from "@/lib/api";
import { useAuth, formatApiErrorDetail, api, API_BASE } from "@/lib/auth";
import {
    SIGNIN_PROMPT_AFTER_MESSAGES,
    JARVIS_SIGNIN_DISMISSED_KEY,
    JARVIS_EMAIL_CAPTURE_DISMISSED_KEY,
} from "@/config/jarvis";

const SESSION_KEY = "mm_jarvis_session";
// Phase 11 B — voice is ON by default. Persisted only when the user explicitly
// clicks the mute button. Fresh browsers hear Jarvis speak.
const MUTE_KEY = "jarvis_muted";

const QUICK_ACTIONS = [
    "Term Life",
    "Whole Life",
    "Final Expense",
    "Medicare",
    "I'm not sure yet",
];

/** Animated Jarvis orb — framer-motion pulse, navy→sky gradient. */
function JarvisOrb({ size = 120 }) {
    return (
        <div
            className="relative mx-auto"
            style={{ width: size, height: size }}
            aria-hidden
        >
            <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-br from-mm-sky via-mm-blue to-mm-navy"
                animate={{ scale: [1, 1.07, 1] }}
                transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute inset-3 rounded-full bg-gradient-to-br from-white/30 via-transparent to-transparent blur-sm"
                animate={{ opacity: [0.5, 0.85, 0.5] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="absolute inset-0 rounded-full ring-1 ring-white/30" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white">
                <Sparkles size={size * 0.28} />
            </div>
        </div>
    );
}

export default function JarvisChat() {
    const { user, signInWithGoogle, signOut, requestMagicLink, verifyToken } = useAuth();
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]); // {id, role, content}
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    // Phase 12 — the hard AuthModal is retired. Email capture is an inline
    // non-blocking banner (same functionality, soft UX).
    const [showEmailCapture, setShowEmailCapture] = useState(false);
    const [emailCaptureDismissed, setEmailCaptureDismissed] = useState(() => {
        try {
            return localStorage.getItem(JARVIS_EMAIL_CAPTURE_DISMISSED_KEY) === "true";
        } catch {
            return false;
        }
    });
    // Hard backstop state — set when the backend returned reason=signin_required
    // on the previous send. The chat input stays disabled with a tooltip until
    // the user signs in. Cleared automatically when `user` becomes truthy.
    const [signInRequired, setSignInRequired] = useState(false);
    useEffect(() => {
        if (user) setSignInRequired(false);
    }, [user]);
    // When the user clicks "or send me a magic link instead" inside the
    // Phase 12 Google prompt, that prompt collapses in favor of the email
    // capture banner for the rest of the session.
    const [googlePromptCollapsed, setGooglePromptCollapsed] = useState(false);
    const [finalizeDone, setFinalizeDone] = useState(false);
    const [summary, setSummary] = useState(null);
    const scrollRef = useRef(null);

    // Phase 8 — chat history for signed-in users only.
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [viewingSessionId, setViewingSessionId] = useState(null); // set when browsing a past session
    const readOnly = Boolean(viewingSessionId);

    // --- Voice state ------------------------------------------------------
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [recordStart, setRecordStart] = useState(null);
    const [recordHint, setRecordHint] = useState(false);
    const [muted, setMuted] = useState(() => {
        // Phase 11 B — voice ON by default. Only persisted mute=true reverses it.
        try {
            return localStorage.getItem(MUTE_KEY) === "true";
        } catch {
            return false;
        }
    });
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRef = useRef(null);

    // Bootstrap — signed-in users always start on a fresh session and load
    // their history sidebar. Anonymous users keep the legacy localStorage
    // restore behavior so a page refresh doesn't lose their conversation.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                if (user) {
                    // Fresh session on every visit for signed-in users.
                    localStorage.removeItem(SESSION_KEY);
                    const created = await jarvisCreateSession();
                    if (!alive) return;
                    setSessionId(created.session_id);
                    localStorage.setItem(SESSION_KEY, created.session_id);
                    setMessages([
                        { id: "opening", role: "assistant", content: created.opening_message },
                    ]);
                    setSummary(null);
                    setFinalizeDone(false);
                    setViewingSessionId(null);
                    // Load past-session list in parallel.
                    loadSessions();
                    return;
                }

                // Anonymous — restore local session if present, else create.
                const stored = localStorage.getItem(SESSION_KEY);
                if (stored) {
                    try {
                        const existing = await jarvisGetSession(stored);
                        if (alive) {
                            setSessionId(stored);
                            setMessages(existing.messages || []);
                            if (existing.summary) setSummary(existing.summary);
                            return;
                        }
                    } catch {
                        localStorage.removeItem(SESSION_KEY);
                    }
                }
                const created = await jarvisCreateSession();
                if (!alive) return;
                setSessionId(created.session_id);
                localStorage.setItem(SESSION_KEY, created.session_id);
                setMessages([
                    { id: "opening", role: "assistant", content: created.opening_message },
                ]);
            } catch {
                toast.error("Couldn't start Jarvis. Please refresh.");
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const loadSessions = useCallback(async () => {
        if (!user) return;
        setSessionsLoading(true);
        try {
            const list = await jarvisListSessions();
            setSessions(Array.isArray(list) ? list : []);
        } catch {
            /* silent — sidebar is a convenience, not a requirement */
        } finally {
            setSessionsLoading(false);
        }
    }, [user]);

    const openPastSession = useCallback(async (id) => {
        try {
            const detail = await jarvisGetMySession(id);
            setViewingSessionId(id);
            setMessages(detail.messages || []);
            setSummary(detail.summary || null);
            setFinalizeDone(false);
            setInput("");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 403) toast.error("That conversation isn't yours.");
            else if (status === 404) toast.error("Conversation not found.");
            else toast.error("Couldn't load that conversation.");
        }
    }, []);

    const backToActive = useCallback(async () => {
        // Return to whatever session is currently active in state.
        if (!sessionId) return;
        setViewingSessionId(null);
        try {
            const current = await jarvisGetSession(sessionId);
            setMessages(current.messages || []);
            setSummary(current.summary || null);
        } catch {
            /* keep whatever we had */
        }
    }, [sessionId]);

    const continueViewedSession = useCallback(async () => {
        if (!viewingSessionId) return;
        try {
            const { session_id: newId } = await jarvisContinueSession(viewingSessionId);
            setSessionId(newId);
            localStorage.setItem(SESSION_KEY, newId);
            // Load the freshly cloned transcript
            const detail = await jarvisGetSession(newId);
            setMessages(detail.messages || []);
            setSummary(detail.summary || null);
            setViewingSessionId(null);
            setFinalizeDone(false);
            toast.success("Picking up where you left off.");
            loadSessions();
        } catch {
            toast.error("Couldn't continue that conversation.");
        }
    }, [viewingSessionId, loadSessions]);

    // Auto-scroll to the newest message.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [messages.length, sending]);

    const userTurnCount = useMemo(
        () => messages.filter((m) => m.role === "user").length,
        [messages],
    );

    const qualified = userTurnCount >= 6;

    // Phase 12 — inline soft sign-in prompt after N user exchanges.
    // Threshold lives in /app/frontend/src/config/jarvis.js. Not a hard
    // gate: Jarvis stays usable even if the prompt is ignored or dismissed.
    const [signInDismissed, setSignInDismissed] = useState(() => {
        try {
            return localStorage.getItem(JARVIS_SIGNIN_DISMISSED_KEY) === "true";
        } catch {
            return false;
        }
    });
    const [googleStarting, setGoogleStarting] = useState(false);
    const showSignInPrompt =
        !user &&
        !signInDismissed &&
        !readOnly &&
        !googlePromptCollapsed &&
        userTurnCount >= SIGNIN_PROMPT_AFTER_MESSAGES;

    // Email capture visibility — hard rule from the brief: ONLY show when the
    // Google prompt isn't visible. Dismissal persists per browser.
    const showEmailCaptureBanner =
        !user &&
        !readOnly &&
        !emailCaptureDismissed &&
        !showSignInPrompt &&
        (showEmailCapture || googlePromptCollapsed);

    const dismissSignInPrompt = useCallback(() => {
        setSignInDismissed(true);
        try { localStorage.setItem(JARVIS_SIGNIN_DISMISSED_KEY, "true"); } catch { /* ignore */ }
    }, []);

    const dismissEmailCapture = useCallback(() => {
        setEmailCaptureDismissed(true);
        setShowEmailCapture(false);
        try { localStorage.setItem(JARVIS_EMAIL_CAPTURE_DISMISSED_KEY, "true"); } catch { /* ignore */ }
    }, []);

    // When user clicks "or send me a magic link instead" inside the Phase 12
    // prompt, collapse it into the inline email capture banner.
    const collapseGoogleToEmail = useCallback(() => {
        setGooglePromptCollapsed(true);
        // User explicitly asked for the email path — ensure the capture
        // banner is visible even if they previously dismissed it.
        setEmailCaptureDismissed(false);
        try { localStorage.removeItem(JARVIS_EMAIL_CAPTURE_DISMISSED_KEY); } catch { /* ignore */ }
    }, []);

    const handleGoogleSignIn = useCallback(async () => {
        setGoogleStarting(true);
        try {
            await signInWithGoogle({
                redirectTo: "/jarvis",
                chatSessionId: sessionId,
            });
        } catch (err) {
            setGoogleStarting(false);
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Couldn't start Google sign-in.",
            );
        }
    }, [signInWithGoogle, sessionId]);

    const handleSend = useCallback(
        async (rawText) => {
            const text = (typeof rawText === "string" ? rawText : input).trim();
            if (!text || !sessionId || sending) return;

            // Optimistic user bubble
            const localId = `u-${Date.now()}`;
            setMessages((prev) => [
                ...prev,
                { id: localId, role: "user", content: text },
            ]);
            setInput("");
            setSending(true);

            try {
                const res = await jarvisSendMessage(sessionId, text);
                if (res.blocked) {
                    // Phase 12 soft-prompt rule: keep the user's bubble
                    // visible, render the backend-provided reason as an
                    // inline assistant message, and surface the sign-in
                    // surface. Only hard-disable the input when the backend
                    // hit the ANON_MESSAGE_LIMIT backstop (reason=signin_required).
                    const reply = res.reply ||
                        "Sign in to keep chatting with Jarvis.";
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: `a-${Date.now()}`,
                            role: "assistant",
                            content: reply,
                            blocked: true,
                        },
                    ]);
                    setShowEmailCapture(true);
                    if (res.reason === "signin_required") {
                        setSignInRequired(true);
                    }
                    return;
                }
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `a-${Date.now()}`,
                        role: "assistant",
                        content: res.reply,
                    },
                ]);
                // Refresh sidebar so the newest activity + title flow in.
                if (user) loadSessions();
            } catch (err) {
                // Phase 12 — no silent bubble-drops. Leave the user's turn
                // visible so they can see what they sent, surface an inline
                // assistant-side error message, and keep the input enabled
                // so they can retry without re-typing the whole message.
                const detail =
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Jarvis couldn't reach the server. Please try again.";
                toast.error(detail);
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `e-${Date.now()}`,
                        role: "assistant",
                        content: `⚠ ${detail}`,
                        errored: true,
                    },
                ]);
            } finally {
                setSending(false);
            }
        },
        [input, sessionId, sending, user, loadSessions],
    );

    const handleReset = async () => {
        try {
            localStorage.removeItem(SESSION_KEY);
            const created = await jarvisCreateSession();
            setSessionId(created.session_id);
            localStorage.setItem(SESSION_KEY, created.session_id);
            setMessages([
                {
                    id: "opening",
                    role: "assistant",
                    content: created.opening_message,
                },
            ]);
            setSummary(null);
            setFinalizeDone(false);
            setViewingSessionId(null);
            toast.success("Fresh conversation started.");
            if (user) loadSessions();
        } catch {
            toast.error("Couldn't reset the session.");
        }
    };

    const handleFinalize = async () => {
        if (!sessionId) return;
        if (!user) {
            // Phase 12 — soft nudge instead of a hard modal gate.
            setShowEmailCapture(true);
            toast.message("Save your summary — enter your email below to continue.");
            return;
        }
        setFinalizing(true);
        try {
            if (!summary) {
                const s = await jarvisSummarize(sessionId);
                setSummary(s);
            }
            const res = await jarvisFinalize(sessionId);
            setFinalizeDone(true);
            toast.success(
                res.hot_flag
                    ? "We'll have an advisor reach out today."
                    : "Saved — an advisor will follow up shortly.",
            );
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Couldn't save your summary just yet.",
            );
        } finally {
            setFinalizing(false);
        }
    };

    // Phase 11 B — track audio playback for the "Playing" indicator.
    const [playing, setPlaying] = useState(false);

    // --- Autoplay-safe deferred playback (P0 fix) ------------------------
    // Modern browsers block HTMLAudioElement.play() with NotAllowedError
    // until the page has received a user gesture. We stash the already-
    // synthesized audio in pendingAudioRef and attach a one-shot gesture
    // listener on the window. First click/keydown/touchstart unlocks and
    // plays the pending clip. From that point on, all subsequent play()
    // calls succeed naturally for the lifetime of the tab.
    const pendingAudioRef = useRef(null);
    const hintTimerRef = useRef(null);
    const gestureInstalledRef = useRef(false);
    // mutedRef mirrors the latest `muted` state so the gesture callback
    // (closed over at install time) sees the current value without stale
    // reads. Fixes the edge case where a user clicks Mute during the wait.
    const mutedRef = useRef(muted);
    useEffect(() => { mutedRef.current = muted; }, [muted]);
    const [awaitingGesture, setAwaitingGesture] = useState(false);
    const [showGestureHint, setShowGestureHint] = useState(false);

    const clearPending = useCallback(() => {
        if (pendingAudioRef.current) {
            try {
                pendingAudioRef.current.pause();
                pendingAudioRef.current.currentTime = 0;
            } catch { /* noop */ }
            pendingAudioRef.current = null;
        }
    }, []);

    const installGestureListener = useCallback(() => {
        if (gestureInstalledRef.current) return;
        gestureInstalledRef.current = true;
        const opts = { capture: true };
        const onGesture = (e) => {
            // If the user clicked the Mute button, their intent was to
            // SILENCE Jarvis, not to unlock audio. Tear everything down
            // without playing. Also respect the latest muted state.
            const target = e && e.target && typeof e.target.closest === "function"
                ? e.target.closest('[data-testid="jarvis-voice-toggle"]')
                : null;
            const treatAsMute = !!target || mutedRef.current === true;

            window.removeEventListener("click", onGesture, opts);
            window.removeEventListener("keydown", onGesture, opts);
            window.removeEventListener("touchstart", onGesture, opts);
            gestureInstalledRef.current = false;
            if (hintTimerRef.current) {
                clearTimeout(hintTimerRef.current);
                hintTimerRef.current = null;
            }
            setAwaitingGesture(false);
            setShowGestureHint(false);

            if (treatAsMute) {
                // Drop the pending audio silently. The subsequent React
                // onClick on the mute button will complete toggleMute, which
                // persists the mute preference.
                clearPending();
                return;
            }

            const pending = pendingAudioRef.current;
            pendingAudioRef.current = null;
            if (pending) {
                audioRef.current = pending;
                // Runs in a gesture callback, so autoplay rules are satisfied.
                pending.play().catch(() => { /* next message will try fresh */ });
            }
        };
        window.addEventListener("click", onGesture, opts);
        window.addEventListener("keydown", onGesture, opts);
        window.addEventListener("touchstart", onGesture, opts);
    }, [clearPending]);

    // --- Voice: TTS auto-play on new assistant replies --------------------
    //
    // Phase 14c — provider pivoted from ElevenLabs to OpenAI (via emergent-
    // integrations). The endpoint can now return TWO shapes:
    //   • audio/mpeg bytes       → play through HTMLAudioElement (normal path)
    //   • application/json       → `{"fallback": true}` — speak via browser
    //                              SpeechSynthesis so the user always hears
    //                              a reply even if the TTS provider is down.
    //
    const speakViaBrowser = useCallback((text) => {
        if (!text) return;
        try {
            const synth = window.speechSynthesis;
            if (!synth) return;
            // Stop anything already queued so we don't stack voices.
            synth.cancel();
            const utter = new SpeechSynthesisUtterance(text);
            // Prefer a male en-GB voice (Daniel / Google UK English Male / George).
            const pickVoice = () => {
                const voices = synth.getVoices() || [];
                if (voices.length === 0) return null;
                const gbMalePatterns = [
                    /daniel/i, /george/i, /oliver/i, /arthur/i,
                    /male/i, /uk english male/i,
                ];
                const gbVoices = voices.filter(
                    (v) => (v.lang || "").toLowerCase().startsWith("en-gb")
                );
                const male = gbVoices.find((v) =>
                    gbMalePatterns.some((p) => p.test(v.name || ""))
                );
                return male || gbVoices[0] || voices[0] || null;
            };
            const voice = pickVoice();
            if (voice) utter.voice = voice;
            utter.lang = (voice && voice.lang) || "en-GB";
            utter.rate = 0.95;
            utter.pitch = 0.9;
            utter.onstart = () => {
                setPlaying(true);
                try { window.__jarvisPlaying = true; } catch { /* noop */ }
            };
            utter.onend = () => {
                setPlaying(false);
                try { window.__jarvisPlaying = false; } catch { /* noop */ }
            };
            utter.onerror = () => {
                setPlaying(false);
                try { window.__jarvisPlaying = false; } catch { /* noop */ }
            };
            synth.speak(utter);
        } catch (err) {
            console.warn("[jarvis tts] speechSynthesis failed", err);
        }
    }, []);

    const playTTS = useCallback(async (text) => {
        if (!text || muted) return;
        // Stop any prior audio so we don't stack.
        if (audioRef.current) {
            try { audioRef.current.pause(); } catch { /* noop */ }
            audioRef.current = null;
        }
        clearPending();
        let audio = null;
        let url = null;
        try {
            const res = await api.post(
                "/jarvis/voice/speak",
                { text, session_id: sessionId },
                { responseType: "blob" },
            );
            const blob = res.data;
            if (!blob || blob.size === 0) return;

            // Phase 14c — detect the JSON fallback body. axios doesn't inspect
            // the content type when responseType=blob, so we read the blob's
            // own `.type` property.
            const blobType = (blob.type || "").toLowerCase();
            if (blobType.startsWith("application/json")) {
                try {
                    const txt = await blob.text();
                    const parsed = JSON.parse(txt);
                    if (parsed && parsed.fallback === true) {
                        speakViaBrowser(text);
                        return;
                    }
                } catch (_err) {
                    // malformed JSON — fall through to the native synth anyway
                    speakViaBrowser(text);
                    return;
                }
            }

            url = URL.createObjectURL(blob);
            audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => {
                URL.revokeObjectURL(url);
                setPlaying(false);
                try { window.__jarvisPlaying = false; } catch { /* noop */ }
            };
            audio.onpause  = () => {
                setPlaying(false);
                try { window.__jarvisPlaying = false; } catch { /* noop */ }
            };
            audio.onplaying = () => {
                setPlaying(true);
                try { window.__jarvisPlaying = true; } catch { /* noop */ }
                // Clearing the hint here handles the case where the gesture
                // listener fired and successfully unlocked playback.
                setAwaitingGesture(false);
                setShowGestureHint(false);
            };
            await audio.play();
        } catch (err) {
            setPlaying(false);
            try { window.__jarvisPlaying = false; } catch { /* noop */ }
            const status = err?.response?.status;
            if (status === 429) {
                toast.error("Voice response limit reached — Jarvis will reply in text for now.");
                return;
            }
            if (status === 503 || status === 502) {
                // Backend declared the TTS pipeline unavailable (should be
                // rare now that we return 200 + fallback). Speak locally.
                speakViaBrowser(text);
                return;
            }
            // Any play()-side rejection where the audio has not yet started
            // playing is treated as autoplay-blocked. Different browsers
            // surface the block as NotAllowedError, AbortError, or a bare
            // DOMException depending on timing (StrictMode double-mount,
            // iOS Safari) — key the decision on the audio state, not the
            // error name.
            const playBlocked =
                audio &&
                audio.paused &&
                (audio.currentTime === 0 || Number.isNaN(audio.currentTime));
            if (playBlocked) {
                // Stash the prepared audio and wait for the first user
                // gesture to unlock it.
                pendingAudioRef.current = audio;
                audioRef.current = null;  // not the "current playing" one yet
                setAwaitingGesture(true);
                if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
                hintTimerRef.current = setTimeout(() => setShowGestureHint(true), 3000);
                installGestureListener();
            } else {
                console.warn("[jarvis tts]", err);
            }
        }
    }, [muted, sessionId, clearPending, installGestureListener, speakViaBrowser]);

    // Clean up the hint timer on unmount so it doesn't fire against a dead
    // component.
    useEffect(() => {
        return () => {
            if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
        };
    }, []);

    // When a new assistant message arrives AND voice isn't muted, speak it.
    // Phase 11 B — voice-on-by-default: this includes the opening welcome
    // message. Browser autoplay policy may block the first play() until a
    // user gesture; playTTS() catches the NotAllowedError silently so the
    // chat still works even when audio is blocked.
    const lastSpokenRef = useRef(null);
    useEffect(() => {
        if (muted) return;
        const last = messages[messages.length - 1];
        if (!last || last.role !== "assistant") return;
        if (last.id === lastSpokenRef.current) return;
        lastSpokenRef.current = last.id;
        playTTS(last.content);
    }, [messages, muted, playTTS]);

    // --- Voice: mic capture + STT → handleSend ---------------------------
    const stopRecording = useCallback(async () => {
        const mr = mediaRecorderRef.current;
        if (!mr || mr.state === "inactive") return;
        try {
            mr.stop();
            mr.stream.getTracks().forEach((t) => t.stop());
        } catch { /* noop */ }
    }, []);

    const startRecording = useCallback(async () => {
        if (recording || transcribing) return;
        if (!navigator.mediaDevices?.getUserMedia) {
            toast.error("Mic not available in this browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Prefer webm/opus; fall back to default codec if unsupported.
            const mime = MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : (MediaRecorder.isTypeSupported?.("audio/webm") ? "audio/webm" : "");
            const mr = mime
                ? new MediaRecorder(stream, { mimeType: mime })
                : new MediaRecorder(stream);
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                setRecording(false);
                setRecordHint(false);
                setRecordStart(null);
                const chunks = audioChunksRef.current;
                audioChunksRef.current = [];
                if (chunks.length === 0) return;
                const blob = new Blob(chunks, { type: mime || "audio/webm" });
                if (blob.size < 500) {
                    toast.error("That was a little too short — try again.");
                    return;
                }
                setTranscribing(true);
                try {
                    const fd = new FormData();
                    fd.append("audio", blob, "speech.webm");
                    if (sessionId) fd.append("session_id", sessionId);
                    const res = await fetch(`${API_BASE}/jarvis/voice/transcribe`, {
                        method: "POST", body: fd,
                        credentials: "include",
                    });
                    if (!res.ok) {
                        const body = await res.json().catch(() => ({}));
                        if (res.status === 429) {
                            toast.error(body.detail || "Voice rate limit reached.");
                        } else if (res.status === 415) {
                            toast.error("Audio format not supported.");
                        } else {
                            toast.error(body.detail || "Couldn't transcribe that — please try again.");
                        }
                        return;
                    }
                    const { transcript } = await res.json();
                    const clean = (transcript || "").trim();
                    if (!clean) {
                        toast.error("I didn't catch that — try again?");
                        return;
                    }
                    // Phase 11 B — mic-touched no longer flips voice default (voice is now on-by-default).
                    // Send through the exact same text pipeline.
                    await handleSend(clean);
                } finally {
                    setTranscribing(false);
                }
            };
            mr.start();
            mediaRecorderRef.current = mr;
            setRecording(true);
            setRecordStart(Date.now());
        } catch (err) {
            if (err?.name === "NotAllowedError") {
                toast.error("Mic permission denied. Check your browser settings.");
            } else {
                toast.error("Couldn't start the mic — please try again.");
            }
        }
    }, [recording, transcribing, sessionId]);

    const toggleMic = useCallback(() => {
        if (recording) stopRecording(); else startRecording();
    }, [recording, startRecording, stopRecording]);

    // Hint after 60s of continuous recording.
    useEffect(() => {
        if (!recording || !recordStart) return;
        const t = setInterval(() => {
            if (Date.now() - recordStart >= 60_000) setRecordHint(true);
        }, 1000);
        return () => clearInterval(t);
    }, [recording, recordStart]);

    const toggleMute = useCallback(() => {
        setMuted((prev) => {
            const next = !prev;
            try { localStorage.setItem(MUTE_KEY, String(next)); } catch { /* ignore */ }
            // Phase 11 B — clicking mute during playback must stop it immediately.
            if (next && audioRef.current) {
                try {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                } catch { /* noop */ }
                audioRef.current = null;
                setPlaying(false);
            }
            // Phase 14c — also cancel any in-flight speechSynthesis utterance
            // so the fallback voice path respects mute instantly.
            if (next) {
                try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
            }
            // P0 autoplay fix — also drop any audio queued waiting on a
            // gesture when the user mutes, and clear the hint.
            if (next) {
                clearPending();
                setAwaitingGesture(false);
                setShowGestureHint(false);
                if (hintTimerRef.current) {
                    clearTimeout(hintTimerRef.current);
                    hintTimerRef.current = null;
                }
            }
            return next;
        });
    }, [clearPending]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            try { mediaRecorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop()); } catch { /* noop */ }
            try { audioRef.current?.pause(); } catch { /* noop */ }
        };
    }, []);

    return (
        <div className="bg-mm-bg" data-testid="jarvis-chat-page">
            <div className="mm-container grid gap-8 py-10 sm:py-14 lg:grid-cols-[0.9fr_1.6fr]">
                {/* Left rail */}
                <aside
                    className="lg:sticky lg:top-24 h-max rounded-3xl bg-gradient-to-br from-mm-navy via-mm-navy to-mm-blue p-8 text-white shadow-card"
                    data-testid="jarvis-left-rail"
                >
                    <JarvisOrb size={112} />
                    <h2 className="mt-6 text-center mm-display text-2xl">Jarvis</h2>
                    <p className="mt-1 text-center text-sm text-white/75">
                        Your personal insurance concierge
                    </p>

                    <ul className="mt-8 space-y-2.5 text-sm text-white/80">
                        <li className="flex gap-2">
                            <span className="text-mm-sky">•</span> We do not sell your data.
                        </li>
                        <li className="flex gap-2">
                            <span className="text-mm-sky">•</span> Powered by advanced AI technology.
                        </li>
                        <li className="flex gap-2">
                            <span className="text-mm-sky">•</span> No-pressure, personalized.
                        </li>
                    </ul>

                    <div className="mt-8 flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={toggleMute}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/[0.06] px-4 py-2.5 text-xs font-medium text-white/85 hover:bg-white/[0.12] transition-colors"
                            data-testid="jarvis-voice-toggle"
                            title={muted ? "Unmute Jarvis" : "Mute Jarvis"}
                            aria-pressed={muted}
                        >
                            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                            {muted ? "Muted" : "Mute"}
                        </button>
                        {playing && !muted && (
                            <div
                                className="inline-flex items-center justify-center gap-1.5 text-[11px] font-medium text-mm-sky"
                                data-testid="jarvis-playing-indicator"
                                aria-live="polite"
                            >
                                <Volume2 size={11} className="animate-pulse" /> Playing
                            </div>
                        )}
                        {showGestureHint && !muted && !playing && (
                            <div
                                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-300/10 px-3 py-1 text-[11px] font-medium text-amber-200"
                                data-testid="jarvis-gesture-hint"
                                aria-live="polite"
                                role="status"
                            >
                                <Volume2 size={11} /> Click anywhere to hear Jarvis
                            </div>
                        )}

                        <button
                            onClick={handleReset}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/[0.06] px-4 py-2.5 text-xs font-medium text-white/85 hover:bg-white/[0.12] transition-colors"
                            data-testid="jarvis-reset-session"
                        >
                            {user ? <PlusCircle size={14} /> : <RefreshCw size={14} />}
                            {user ? "New chat" : "Reset conversation"}
                        </button>
                    </div>

                    {/* Phase 8 — Chat history (signed-in users only) */}
                    {user && (
                        <div className="mt-8 border-t border-white/10 pt-6" data-testid="jarvis-history-panel">
                            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/70">
                                <History size={12} /> Your conversations
                            </div>
                            <div
                                className="mt-3 max-h-80 space-y-1 overflow-y-auto pr-1"
                                data-testid="jarvis-history-list"
                            >
                                {sessionsLoading && sessions.length === 0 && (
                                    <div className="flex items-center gap-2 rounded-lg bg-white/[0.05] px-3 py-2 text-xs text-white/60" data-testid="jarvis-history-loading">
                                        <Loader2 size={12} className="animate-spin" /> Loading history…
                                    </div>
                                )}
                                {!sessionsLoading && sessions.length === 0 && (
                                    <div className="rounded-lg bg-white/[0.05] px-3 py-2 text-[11px] text-white/55" data-testid="jarvis-history-empty">
                                        No past conversations yet. Start chatting — Jarvis remembers.
                                    </div>
                                )}
                                {sessions.map((s) => {
                                    const isCurrent = s.id === sessionId && !viewingSessionId;
                                    const isViewing = s.id === viewingSessionId;
                                    return (
                                        <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => (isCurrent ? backToActive() : openPastSession(s.id))}
                                            className={`w-full rounded-lg px-3 py-2 text-left text-xs transition-colors ${
                                                isViewing
                                                    ? "bg-white/[0.14] text-white ring-1 ring-white/40"
                                                    : isCurrent
                                                        ? "bg-mm-sky/25 text-white"
                                                        : "bg-white/[0.04] text-white/80 hover:bg-white/[0.10]"
                                            }`}
                                            data-testid={`jarvis-history-item-${s.id}`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="truncate font-medium">{s.title || "New conversation"}</span>
                                                {isCurrent && (
                                                    <span className="shrink-0 rounded-full bg-mm-sky/40 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white" data-testid={`jarvis-history-current-${s.id}`}>
                                                        Now
                                                    </span>
                                                )}
                                                {isViewing && (
                                                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-500/70 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white" data-testid={`jarvis-history-viewing-${s.id}`}>
                                                        <Eye size={9} /> Read-only
                                                    </span>
                                                )}
                                            </div>
                                            {s.last_message_preview && (
                                                <div className="mt-0.5 truncate text-[10px] text-white/55">
                                                    {s.last_message_preview}
                                                </div>
                                            )}
                                            <div className="mt-0.5 text-[9px] uppercase tracking-wider text-white/40">
                                                {s.message_count} msg · {formatRelative(s.updated_at)}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <p className="mt-8 text-[10px] leading-relaxed text-white/50">
                        Jarvis never guarantees approval, rates, or outcomes. A licensed advisor reviews every recommendation.
                    </p>
                </aside>

                {/* Chat pane */}
                <section
                    className="mm-card flex min-h-[580px] flex-col overflow-hidden"
                    data-testid="jarvis-chat-pane"
                >
                    {/* header strip */}
                    <div className="flex items-center justify-between border-b border-mm-line px-5 py-3">
                        <div className="flex items-center gap-2 text-sm text-mm-muted">
                            <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-mm-blue" />
                            Jarvis · online
                        </div>
                        {user ? (
                            <div
                                className="group inline-flex items-center gap-2 rounded-full border border-mm-line bg-white px-2 py-1 text-xs text-mm-ink"
                                data-testid="jarvis-signed-in-pill"
                            >
                                {user.profile_picture_url ? (
                                    <img
                                        src={user.profile_picture_url}
                                        alt=""
                                        className="h-5 w-5 rounded-full object-cover"
                                        referrerPolicy="no-referrer"
                                    />
                                ) : (
                                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-mm-navy text-[10px] font-semibold uppercase text-white">
                                        {(user.first_name || user.email || "?").slice(0, 1)}
                                    </span>
                                )}
                                <span className="max-w-[160px] truncate font-medium" data-testid="jarvis-signed-in-label">
                                    {user.first_name || user.email}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => signOut()}
                                    className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-mm-muted transition-colors hover:bg-mm-bg hover:text-mm-navy"
                                    aria-label="Sign out"
                                    title="Sign out"
                                    data-testid="jarvis-signed-in-logout"
                                >
                                    <LogOut size={11} />
                                </button>
                            </div>
                        ) : (
                            <div
                                className="text-[10px] uppercase tracking-[0.18em] text-mm-muted"
                                data-testid="jarvis-guest-label"
                            >
                                Guest
                            </div>
                        )}
                    </div>

                    {/* messages */}
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto px-5 py-6 space-y-3"
                        data-testid="jarvis-messages"
                    >
                        {readOnly && (
                            <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="sticky top-0 z-10 -mx-5 -mt-6 mb-2 border-b border-mm-line bg-white/95 px-5 py-3 backdrop-blur"
                                data-testid="jarvis-readonly-banner"
                            >
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-2 text-xs text-mm-muted">
                                        <Eye size={14} className="text-amber-600" />
                                        <span>
                                            Read-only — this is a past conversation.
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={backToActive}
                                            className="inline-flex items-center gap-1 rounded-full border border-mm-line bg-white px-3 py-1.5 text-xs font-medium text-mm-navy hover:bg-mm-bg"
                                            data-testid="jarvis-readonly-back"
                                        >
                                            Back to current
                                        </button>
                                        <button
                                            type="button"
                                            onClick={continueViewedSession}
                                            className="mm-btn-primary inline-flex items-center gap-1 !px-3 !py-1.5 !text-xs"
                                            data-testid="jarvis-continue-btn"
                                        >
                                            <Play size={12} /> Continue this conversation
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                        <AnimatePresence initial={false}>
                            {messages.map((m) => (
                                <MessageBubble key={m.id} role={m.role} content={m.content} />
                            ))}
                        </AnimatePresence>
                        {sending && !readOnly && <TypingIndicator />}

                        {qualified && !finalizeDone && !readOnly && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-6 rounded-2xl border border-mm-line bg-mm-bg p-4"
                                data-testid="jarvis-finalize-cta"
                            >
                                <p className="text-sm font-medium text-mm-navy">
                                    Ready to save your summary and book a time?
                                </p>
                                <p className="mt-1 text-xs text-mm-muted">
                                    A licensed advisor will personally review what you've shared and reach out to book a conversation that fits your schedule.
                                </p>
                                <button
                                    onClick={handleFinalize}
                                    disabled={finalizing}
                                    className="mm-btn-primary mt-3 !py-2.5 disabled:opacity-60"
                                    data-testid="jarvis-finalize-button"
                                >
                                    {finalizing ? (
                                        <>
                                            <Loader2 size={14} className="animate-spin" />
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <CalendarCheck size={14} />
                                            Save my summary & book a time
                                        </>
                                    )}
                                </button>
                            </motion.div>
                        )}

                        {showSignInPrompt && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-6 rounded-2xl border border-mm-sky/30 bg-gradient-to-br from-white to-mm-sky/5 p-4"
                                data-testid="jarvis-signin-prompt"
                            >
                                <p className="text-sm font-semibold text-mm-navy">
                                    Save this chat?
                                </p>
                                <p className="mt-1 text-xs text-mm-muted">
                                    Sign in with Google so we can send your personalized quote and you can pick up where you left off.
                                </p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={handleGoogleSignIn}
                                        disabled={googleStarting}
                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-mm-line bg-white px-4 py-2 text-xs font-medium text-mm-ink shadow-sm transition-colors hover:bg-mm-bg disabled:opacity-60"
                                        data-testid="jarvis-signin-google"
                                    >
                                        {googleStarting ? (
                                            <Loader2 size={12} className="animate-spin" />
                                        ) : (
                                            <GoogleGlyph size={12} />
                                        )}
                                        Continue with Google
                                    </button>
                                    <button
                                        type="button"
                                        onClick={dismissSignInPrompt}
                                        className="rounded-full border border-transparent px-3 py-2 text-xs text-mm-muted transition-colors hover:bg-mm-bg hover:text-mm-ink"
                                        data-testid="jarvis-signin-dismiss"
                                    >
                                        No thanks
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={collapseGoogleToEmail}
                                    className="mt-2 text-[11px] text-mm-blue underline-offset-2 hover:underline"
                                    data-testid="jarvis-signin-switch-email"
                                >
                                    or send me a magic link instead
                                </button>
                            </motion.div>
                        )}

                        {showEmailCaptureBanner && (
                            <EmailCaptureBanner
                                onDismiss={dismissEmailCapture}
                                onVerified={() => {
                                    setShowEmailCapture(false);
                                    setGooglePromptCollapsed(false);
                                }}
                            />
                        )}

                        {finalizeDone && (
                            <div
                                className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
                                data-testid="jarvis-finalize-done"
                            >
                                Your summary has been saved. A licensed Moving Mountains advisor will reach out personally.
                            </div>
                        )}
                    </div>

                    {/* quick actions */}
                    {!readOnly && (
                        <div
                            className="flex flex-wrap gap-2 border-t border-mm-line bg-white px-5 py-3"
                            data-testid="jarvis-quick-actions"
                        >
                            {QUICK_ACTIONS.map((q) => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => handleSend(q)}
                                    disabled={sending}
                                    className="rounded-full border border-mm-line bg-white px-3 py-1.5 text-xs text-mm-navy transition-colors hover:border-mm-navy/40 hover:bg-mm-bg disabled:opacity-50"
                                    data-testid={`jarvis-quick-${q.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* input */}
                    {!readOnly && recordHint && (
                        <div
                            className="border-t border-mm-line bg-amber-50 px-5 py-1.5 text-[11px] text-amber-800"
                            data-testid="jarvis-record-hint"
                        >
                            You can keep going or tap the mic to stop.
                        </div>
                    )}
                    {!readOnly && (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSend();
                            }}
                            className="flex items-end gap-2 border-t border-mm-line bg-white px-5 py-4"
                            data-testid="jarvis-input-form"
                        >
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            placeholder={
                                signInRequired
                                    ? "Sign in to continue chatting with Jarvis."
                                    : recording
                                        ? "Listening… tap the mic to stop."
                                        : transcribing
                                            ? "Transcribing…"
                                            : "Type your message… (Enter to send, Shift+Enter for newline)"
                            }
                            rows={1}
                            className="max-h-32 min-h-[44px] flex-1 resize-none rounded-xl border border-mm-line bg-mm-bg px-4 py-2.5 text-sm text-mm-ink placeholder:text-mm-muted focus:border-mm-blue focus:outline-none focus:ring-2 focus:ring-mm-blue/25 disabled:cursor-not-allowed disabled:opacity-60"
                            disabled={sending || recording || transcribing || signInRequired}
                            title={signInRequired ? "Sign in to continue" : undefined}
                            data-testid="jarvis-input"
                        />
                        <button
                            type="button"
                            onClick={toggleMic}
                            disabled={sending || transcribing || signInRequired}
                            aria-label={recording ? "Stop recording" : "Start recording"}
                            title={signInRequired ? "Sign in to continue" : recording ? "Stop recording" : "Speak to Jarvis"}
                            className={`relative inline-flex h-[44px] w-[44px] items-center justify-center rounded-xl border transition-colors ${
                                recording
                                    ? "border-rose-500 bg-rose-500 text-white"
                                    : transcribing
                                        ? "border-mm-line bg-mm-bg text-mm-muted"
                                        : "border-mm-line bg-white text-mm-navy hover:bg-mm-bg"
                            } disabled:opacity-50`}
                            data-testid="jarvis-mic-button"
                        >
                            {transcribing ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : recording ? (
                                <>
                                    <MicOff size={16} />
                                    <span className="absolute inset-0 animate-ping rounded-xl border-2 border-rose-400 opacity-50" aria-hidden />
                                </>
                            ) : (
                                <Mic size={16} />
                            )}
                        </button>
                        <button
                            type="submit"
                            disabled={sending || !input.trim() || recording || transcribing || signInRequired}
                            className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-xl bg-mm-navy text-white transition-colors hover:bg-[#08304f] disabled:opacity-40"
                            aria-label="Send"
                            title={signInRequired ? "Sign in to continue" : undefined}
                            data-testid="jarvis-send-button"
                        >
                            {sending ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Send size={16} />
                            )}
                        </button>
                    </form>
                    )}
                </section>
            </div>
        </div>
    );
}

function MessageBubble({ role, content }) {
    const isUser = role === "user";
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex ${isUser ? "justify-end" : "justify-start"}`}
        >
            <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isUser
                        ? "bg-mm-navy text-white"
                        : "relative border border-mm-line bg-white text-mm-ink shadow-[0_1px_2px_rgba(10,37,64,0.04)]"
                }`}
                data-testid={`jarvis-msg-${role}`}
            >
                {!isUser && (
                    <span className="absolute left-0 top-3 h-8 w-[3px] -translate-x-0.5 rounded-full bg-gradient-to-b from-mm-sky to-mm-blue" />
                )}
                {content}
            </div>
        </motion.div>
    );
}

function TypingIndicator() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
            data-testid="jarvis-typing"
        >
            <div className="relative flex items-center gap-1.5 rounded-2xl border border-mm-line bg-white px-4 py-3">
                <span className="absolute left-0 top-3 h-8 w-[3px] -translate-x-0.5 rounded-full bg-gradient-to-b from-mm-sky to-mm-blue" />
                {[0, 1, 2].map((i) => (
                    <motion.span
                        key={i}
                        className="h-1.5 w-1.5 rounded-full bg-mm-navy/60"
                        animate={{ y: [0, -3, 0], opacity: [0.35, 1, 0.35] }}
                        transition={{
                            duration: 0.9,
                            repeat: Infinity,
                            delay: i * 0.15,
                        }}
                    />
                ))}
            </div>
        </motion.div>
    );
}

/** Short relative-time formatter used by the chat-history sidebar. */
function formatRelative(iso) {
    if (!iso) return "just now";
    const t = typeof iso === "string" ? Date.parse(iso) : Number(iso);
    if (!Number.isFinite(t)) return "just now";
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
    return new Date(t).toLocaleDateString();
}

/* Phase 12 — tiny inline Google "G" mark for the inline sign-in prompt. */
function GoogleGlyph({ size = 14 }) {
    return (
        <svg aria-hidden="true" width={size} height={size} viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            <path fill="none" d="M0 0h48v48H0z" />
        </svg>
    );
}

/* Phase 12 — inline soft email-capture banner that replaced the hard
 * AuthModal. Non-blocking: chat input stays enabled when this is shown.
 */
function EmailCaptureBanner({ onDismiss, onVerified }) {
    const { requestMagicLink, verifyToken } = useAuth();
    const [email, setEmail] = useState("");
    const [stage, setStage] = useState("form");
    const [devLink, setDevLink] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSend = async (e) => {
        e?.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            const res = await requestMagicLink(email.trim());
            setDevLink(res?.dev_link || null);
            setStage("sent");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Couldn't send link. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDevVerify = async () => {
        if (!devLink) return;
        try {
            const url = new URL(devLink);
            const token = url.searchParams.get("token");
            if (!token) return;
            setLoading(true);
            const u = await verifyToken(token);
            toast.success(`Welcome, ${u.name || u.email}`);
            onVerified?.(u);
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Couldn't verify the link.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 rounded-2xl border border-mm-line bg-white p-4"
            data-testid="jarvis-email-capture"
        >
            <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-mm-navy/10 text-mm-navy">
                    <Mail size={14} />
                </div>
                <div className="min-w-0 flex-1">
                    {stage === "form" ? (
                        <>
                            <p className="text-sm font-semibold text-mm-navy">One quick step.</p>
                            <p className="mt-0.5 text-xs text-mm-muted">
                                Continue to personalize your experience and save progress.
                            </p>
                            <form onSubmit={handleSend} className="mt-3 flex flex-wrap items-center gap-2">
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="flex-1 min-w-[180px] rounded-full border border-mm-line bg-white px-3.5 py-2 text-sm text-mm-ink placeholder-mm-muted/70 focus:border-mm-blue focus:outline-none focus:ring-2 focus:ring-mm-blue/20"
                                    data-testid="jarvis-email-capture-input"
                                />
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="mm-btn-primary !py-2 text-xs disabled:opacity-60"
                                    data-testid="jarvis-email-capture-send"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={12} className="animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        "Send me a magic link"
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={onDismiss}
                                    className="rounded-full px-3 py-2 text-xs text-mm-muted transition-colors hover:text-mm-ink"
                                    data-testid="jarvis-email-capture-dismiss"
                                >
                                    Dismiss
                                </button>
                            </form>
                            <p className="mt-2 text-[11px] text-mm-muted">
                                We do not sell your data. This takes 15 seconds.
                            </p>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                                <CheckCircle2 size={14} /> Sign-in link sent.
                            </div>
                            <p className="mt-0.5 text-xs text-mm-muted">
                                We sent a one-time sign-in link to {email}. It expires in 15 minutes.
                            </p>
                            {devLink && (
                                <button
                                    onClick={handleDevVerify}
                                    disabled={loading}
                                    className="mm-btn-outline mt-3 !py-2 text-xs disabled:opacity-60"
                                    data-testid="jarvis-email-capture-dev-verify"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={12} className="animate-spin" />
                                            Verifying…
                                        </>
                                    ) : (
                                        <>
                                            <ExternalLink size={12} /> Dev: open magic link
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </motion.div>
    );
}

